/**
 * 数组工具函数
 */

import Decimal from 'decimal.js'

interface TreeNodeMap {
  [key: number]: any
}

interface BuildTreeResult {
  arr: any[]
  map: TreeNodeMap
}

/**
 * 数组转换为树形结构
 */
export function buildTree(
  data: any[],
  id: string = 'id',
  pId: string = 'pId',
  children: string = 'children'
): BuildTreeResult {
  const map: TreeNodeMap = {}
  const result: any[] = []

  // 首先将所有节点放入 map 中
  data.forEach((item) => {
    item[children] = []
    map[item[id]] = item
  })

  // 然后根据 pId 关联子节点
  data.forEach((item) => {
    if (item[pId] !== null) {
      const parent = map[item[pId]]
      if (parent) {
        parent[children].push(map[item[id]])
      }
    } else {
      result.push(map[item[id]])
    }
  })

  return {
    arr: result,
    map
  }
}

interface ArrReduce {
  arr: any[]
  ver?: 'lt' | 'gt'
}

/**
 * 将数组里的某个属性值进行精确累加
 */
export function arrReduce(opt: ArrReduce): Decimal {
  const { arr } = opt
  return arr.reduce<Decimal>((sum, item: any): Decimal => {
    if (item.sumMoney) {
      return sum.add(item.sumMoney())
    }
    return sum
  }, new Decimal(0))
}

/**
 * 计算数组内所有支出总和
 */
export function arrReduceTwo(opt: ArrReduce): Decimal {
  const { arr } = opt
  return arr.reduce<Decimal>((sum, item: any): Decimal => {
    if (item.money) {
      if (item.type === 1) return sum.add(item.money)
    }
    return sum
  }, new Decimal(0))
}

/**
 * 计算数组内根据 money 对比 0 返回总和
 */
export function arrReduceSum(opt: ArrReduce): Decimal {
  const { arr } = opt
  return arr.reduce<Decimal>((sum, item: any): Decimal => {
    if (item.money) {
      if (item.money[opt.ver!](0)) return sum.add(item.money)
    }
    return sum
  }, new Decimal(0))
}

/**
 * 将对象数组里的某个属性值转换成 Decimal 对象
 */
export function updStringToDecimal<T>(data: Array<T>, key: string = 'money'): Array<T> {
  for (let index = 0; index < data.length; index++) {
    const item = data[index] as any
    if (item[key]) {
      item[key] = new Decimal(item[key])
    }
  }
  return data
}

/**
 * 一维数组转二维数组
 */
export function convertToTwoDimensionalArray(data: any[], fIdKey: string = 'pId'): any[] {
  // 先按 sort 字段排序
  const sortedData = [...data].sort((a, b) => {
    if (typeof a.sort === 'number' && typeof b.sort === 'number') {
      return a.sort - b.sort
    }
    if (typeof a.sort === 'number' && typeof b.sort !== 'number') {
      return -1
    }
    if (typeof a.sort !== 'number' && typeof b.sort === 'number') {
      return 1
    }
    return 0
  })

  // 分离第一层和第二层数据
  const firstLevel = sortedData.filter((item) => !item[fIdKey])
  const secondLevel = sortedData.filter((item) => item[fIdKey] !== 0)

  // 构建二维数组
  const result: any[] = []

  // 添加第一层数据
  firstLevel.forEach((item) => {
    const row = {
      ...item,
      children: []
    }

    // 查找并添加子项
    row.children = secondLevel
      .filter((child) => child[fIdKey] === item.id)
      .sort((a, b) => {
        if (typeof a.sort === 'number' && typeof b.sort === 'number') {
          return a.sort - b.sort
        }
        if (typeof a.sort === 'number' && typeof b.sort !== 'number') {
          return -1
        }
        if (typeof a.sort !== 'number' && typeof b.sort === 'number') {
          return 1
        }
        return 0
      })
    
    if (row.children.length === 0) {
      delete row.children
    }
    result.push(row)
  })

  return result
}

/**
 * 数组对象按指定属性升序排序，无属性/属性无效的元素排在最后
 */
export function sortByPropWithInvalidLast(arr: any[], prop: string): any[] {
  return [...arr].sort((a, b) => {
    const parseValidVal = (obj: any) => {
      const val = obj[prop]
      if (typeof val === 'number' && !isNaN(val)) return val
      if (typeof val === 'string' && !isNaN(Number(val))) return Number(val)
      return Infinity
    }

    const valA = parseValidVal(a)
    const valB = parseValidVal(b)

    return valA - valB
  })
}

/**
 * 处理 SKU 数据：按 groupId 分类相邻 + 同组 sort 升序排序
 */
export function processSkuData(data: any[]): { sortedData: any[]; groupCount: Record<string, number> } {
  const copyData = JSON.parse(JSON.stringify(data))

  // 核心排序
  copyData.sort((a: any, b: any) => {
    if (a.groupId === null && b.groupId !== null) return 1
    if (a.groupId !== null && b.groupId === null) return -1
    if (a.groupId === null && b.groupId === null) return 0
    if (a.groupId === b.groupId) return a.sort - b.sort
    return String(a.groupId).localeCompare(String(b.groupId), undefined, {
      numeric: true,
      sensitivity: 'base'
    })
  })

  // 统计每个 groupId 的数量
  const groupCount: Record<string, number> = {}
  copyData.forEach((item: any) => {
    const key = item.groupId === null ? 'null' : String(item.groupId)
    groupCount[key] = (groupCount[key] || 0) + 1
  })

  return {
    sortedData: copyData,
    groupCount
  }
}
